import { useEffect, useRef, useState, type ReactNode } from "react";

import { useFocusTrap } from "@/hooks/use-focus-trap";
import { cn } from "@/lib/utils";

/** Off-canvas drawer for the compact (mobile/tablet) shell — slides in over the
 * workspace from the left or right with a dimming scrim, à la Obsidian mobile.
 *
 * Closing is forgiving: tap the scrim, press Escape, hit the panel's own close
 * control, or swipe the panel toward its edge. The swipe follows the finger but
 * locks to the horizontal axis, so vertical list scrolling inside the drawer is
 * never hijacked. */
export function Drawer({
  open,
  side,
  label,
  onClose,
  children,
}: {
  open: boolean;
  side: "left" | "right";
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  // Keep the panel mounted through its exit transition, then unmount.
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const [drag, setDrag] = useState(0); // live swipe offset in px
  const touch = useRef<{ x: number; y: number; axis: null | "x" | "y" } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, open);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const r = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(r);
    }
    setShown(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  const sign = side === "left" ? -1 : 1;
  const transform = shown ? `translateX(${drag}px)` : `translateX(${sign * 100}%)`;

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    touch.current = { x: t.clientX, y: t.clientY, axis: null };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const st = touch.current;
    const t = e.touches[0];
    if (!st || !t) return;
    const dx = t.clientX - st.x;
    const dy = t.clientY - st.y;
    if (st.axis === null) {
      if (Math.abs(dx) > Math.abs(dy) + 6) st.axis = "x";
      else if (Math.abs(dy) > Math.abs(dx) + 6) st.axis = "y";
      else return;
    }
    if (st.axis !== "x") return; // vertical → let the inner list scroll
    setDrag(side === "left" ? Math.min(0, dx) : Math.max(0, dx));
  };
  const onTouchEnd = () => {
    const st = touch.current;
    touch.current = null;
    if (st?.axis === "x") {
      const past = side === "left" ? drag < -70 : drag > 70;
      if (past) onClose();
    }
    setDrag(0);
  };

  return (
    <>
      <div
        aria-hidden
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-40 bg-black/55 backdrop-blur-[2px] transition-opacity duration-300",
          shown ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTransitionEnd={() => {
          if (!shown) setMounted(false);
        }}
        style={{ transform }}
        className={cn(
          "fixed inset-y-0 z-50 flex w-[min(88vw,360px)] flex-col bg-card shadow-deep",
          // Re-enable the slide transition only when the finger isn't dragging.
          drag === 0 && "transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
          side === "left" ? "left-0 border-r border-border pl-safe" : "right-0 border-l border-border pr-safe",
        )}
      >
        {children}
      </div>
    </>
  );
}
